'use client';
import { Table } from 'antd';
import { Entering, LoadingLogo, Pagination, PrettyCurrency, Typography } from 'components';
import { EmptyInboxClipArt } from 'lib/svgs/ClipArts';
import { useTranslations } from 'next-intl';
import FiltersTransactions from './_components/FiltersTransactions';

import { InfoHollowIcon } from 'lib/svgs/Icons';
import 'styles/sass/ant-design/ant-design-table-checkbox.scss';
import DownloadTransactions from './_components/DownloadTransactions';
import FilterSortTransactions from './_components/FilterSortTransactions';
import { useTransactionsContext } from './_context/TransactionsContext';
import { DataTransactionsTreeType } from './_context/useTableTransactions';

export default function TransactionsPage() {
  const t = useTranslations();

  const {
    isLoading,
    isPending,
    page,
    setPage,
    totalPages,
    isRetrieving,
    control,
    filtersAdded,
    handleClear,
    rows,
    columns,
    transactions,
    transactionStats
  } = useTransactionsContext();

  return (
    <div className="mt-5 flex flex-col gap-4">
      <Entering offset={5} delay={0.6} duration={0.2} position="bottom">
        <div className="mt-1">
          <div className="rounded-t-md bg-white shadow-custom">
            <div className="flex justify-between !p-4 !px-6">
              <Typography
                variant="medium1"
                className="relative left-0 top-0 flex items-center text-tertiary-100"
              >
                <span className="flex gap-2">
                  {t('billing.transactions.title')}
                  <span className="text-tertiary-200">({transactions?.nodesCount})</span>
                </span>
                <a
                  data-tooltip-id="tolq-info-top-start"
                  data-tooltip-html={t('billing.payments.info')}
                >
                  <InfoHollowIcon className="scale-[0.5] cursor-pointer pb-1" />
                </a>
              </Typography>
              <span className="flex gap-4">
                <DownloadTransactions />
                <FilterSortTransactions />
              </span>
            </div>
            <FiltersTransactions
              control={control}
              filtersAdded={filtersAdded}
              onClear={handleClear}
            />
            {(isLoading || isPending) && (
              <div className="grid h-[50vh] flex-1 place-content-center">
                <div className="relative flex flex-col items-center justify-center">
                  <LoadingLogo size="x-small" className="absolute -left-8" />
                </div>
              </div>
            )}

            {!(isLoading || isPending) && rows && (
              <Table<DataTransactionsTreeType>
                className="bg-tertiary-1000 pt-2"
                key={`${page}`}
                tableLayout="auto"
                columns={columns}
                dataSource={rows}
                pagination={false}
                scroll={{
                  x: true
                }}
              />
            )}
            {!(isLoading || isPending) && (!rows || rows.length === 0) && (
              <div className="grid h-[50vh] flex-1 place-content-center">
                <div className="flex flex-col items-center justify-center">
                  <EmptyInboxClipArt />
                  <Typography
                    variant="light3"
                    className="flex max-w-[250px] text-center text-tertiary-200"
                  >
                    {t('billing.transactions.empty')}
                  </Typography>
                </div>
              </div>
            )}
            <div className="flex justify-around">
              {!!totalPages && !!page && !isRetrieving && (
                <Pagination
                  totalPages={totalPages}
                  changePage={setPage}
                  page={page}
                  className="flex justify-center pb-8 pt-6"
                />
              )}
              <span className="flex">
                <Typography
                  variant="regular4xl"
                  className="flex justify-center pb-6 pt-4 text-tertiary-200"
                >
                  {t('billing.total')}:
                </Typography>
                <Typography variant="medium4xl" className="flex justify-center pb-6 pt-4">
                  {transactionStats?.length ? (
                    <>
                      {transactionStats?.map(({ balance, currency }, i) => (
                        <span className="ml-2 text-tertiary-100" key={`${balance}-${i}`}>
                          <PrettyCurrency amount={balance} currency={currency} />
                        </span>
                      ))}
                    </>
                  ) : (
                    <span className="ml-2 text-tertiary-200">N/A</span>
                  )}
                </Typography>
              </span>
            </div>
          </div>
        </div>
        <div className="flex justify-center py-10" />
      </Entering>
    </div>
  );
}
